const RELEASE_META_NAME = "netiv-release";
const RELEASE_STORAGE_KEY = "netiv:release-version";
const KNOWN_CACHE_PREFIXES = ["netiv", "workbox"];

function releaseVersion(): string {
  return (
    document
      .querySelector(`meta[name="${RELEASE_META_NAME}"]`)
      ?.getAttribute("content")
      ?.trim() || "unknown"
  );
}

function cacheBelongsToNetiv(name: string): boolean {
  const normalized = name.toLowerCase();
  return KNOWN_CACHE_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}-`),
  );
}

/**
 * Remove client-side caches when a new NETIV release reaches the browser.
 *
 * The production server already marks HTML as no-store and Vite emits hashed
 * asset names. This closes the remaining gap for an older service worker or
 * Cache Storage entry that could otherwise keep serving the previous shell.
 */
export async function invalidateStaleClientCaches(): Promise<void> {
  const version = releaseVersion();
  let previousVersion = "";

  try {
    previousVersion = window.localStorage.getItem(RELEASE_STORAGE_KEY) ?? "";
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }

  if (previousVersion === version) return;

  const cleanup: Promise<unknown>[] = [];

  if ("caches" in window) {
    try {
      const names = await window.caches.keys();
      cleanup.push(
        ...names
          .filter(cacheBelongsToNetiv)
          .map((name) => window.caches.delete(name)),
      );
    } catch {
      // Cache Storage is optional; the app can still start without it.
    }
  }

  if ("serviceWorker" in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      cleanup.push(...registrations.map((registration) => registration.unregister()));
    } catch {
      // Service workers are optional; the app can still start without them.
    }
  }

  try {
    window.localStorage.setItem(RELEASE_STORAGE_KEY, version);
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }

  await Promise.allSettled(cleanup);
}